import React from 'react'
import { Link } from 'react-router-dom'
import { COMPANY_INFO } from '../lib/constants'
import { getOptimizedImageUrl } from '../lib/cms'
import PriceBanner from '../components/PriceBanner'
import ContactForm from '../components/ContactForm'
import ContactFormMinimal from '../components/ContactFormMinimal'

export default function Home() {
  // Scraped authentic images
  const vanFront = 'https://assets.auxodev.dk/assets/ea5c97a8-a563-48d7-8fa0-939f59c0351d/fd248068-b038-4b6e-bb81-80419e4e7af8.jpg'
  const vanRear = 'https://assets.auxodev.dk/assets/ea5c97a8-a563-48d7-8fa0-939f59c0351d/197fcec7-f684-426c-9d53-5408d6004b06.jpg'

  // The 8 mandatory USPs with coherent FA icons. Notice: Non-clickable, so NO hover effects per requirement!
  const USPs = [
    {
      title: '25 års erfaring',
      desc: 'Høj faglig erfaring som serviceelektriker, nybyggeri og renovering.',
      icon: 'fa-solid fa-award',
    },
    {
      title: 'Egen saxelift',
      desc: 'Max 8m arbejdshøjde – kan køre igennem en almindelig standard dør.',
      icon: 'fa-solid fa-arrows-up-down',
    },
    {
      title: 'Egen bomlift',
      desc: 'Mobil 10,5m Omme bomlift – kan foldes og komme gennem en havelåge.',
      icon: 'fa-solid fa-truck-ramp-box',
    },
    {
      title: 'Effektivt kabeltrækkersystem',
      desc: 'Monteret på minilæsser. Trækker op til 3 kabler ad gangen, ca. 2 km/dag.',
      icon: 'fa-solid fa-wave-pulse',
    },
    {
      title: 'Både private og erhverv',
      desc: 'Løser alt fra private installationer til store entrepriser over 3 år.',
      icon: 'fa-solid fa-building-user',
    },
    {
      title: '485 kr. ex. moms / time',
      desc: 'Fast og gennemskuelig timepris inklusiv servicevogn. En time er en time.',
      icon: 'fa-solid fa-van-shuttle',
    },
    {
      title: 'Ingen skjulte tillæg',
      desc: 'Ingen ubehagelige overraskelser, dyre kørselstillæg eller faste gebyrer.',
      icon: 'fa-solid fa-handshake-simple',
    },
    {
      title: 'Fuldt forsikret',
      desc: 'Fuld dækning ved el-installationer, arbejde i højden og graveopgaver.',
      icon: 'fa-solid fa-shield-halved',
    },
  ]

  const serviceGrid = [
    {
      title: 'Almindelige El-opgaver',
      slug: '/el-arbejde',
      badge: 'Private & Erhverv',
      desc: 'Alle former for almindeligt el-arbejde i private hjem og erhverv. Samarbejde med el-installatør ved autorisation.',
      icon: 'fa-solid fa-plug-circle-bolt',
    },
    {
      title: 'Lange kabeltræk',
      slug: '/kabeltraek',
      badge: 'Specialist',
      desc: 'Eget kabelaftrækkersystem på minilæsser. Trækker op til 3 kabler samtidigt og ca. 2 km kabel om dagen.',
      icon: 'fa-solid fa-network-wired',
    },
    {
      title: 'Liftarbejde i højden',
      slug: '/liftarbejde',
      badge: 'Eget udstyr',
      desc: '8m saxelift og 10,5m mobil Omme bomlift. Spar dyr udlejer. El-arbejde, tagrender og træbeskæring.',
      icon: 'fa-solid fa-arrows-up-down',
    },
    {
      title: 'Designerlamper',
      slug: '/designerlamper',
      badge: 'Dansk design',
      desc: 'Professionel montering og rådgivning om placering. Tilbud på danske designerlamper inkl. montage.',
      icon: 'fa-regular fa-lightbulb',
    },
  ]

  return (
    <div className="space-y-0">
      {/* SECTION 1: HERO BANNER (50/50 Split based on Design Reference #eca37617) */}
      <section className="relative bg-[#F2F2F0] border-b border-[#292A30]/15 overflow-hidden">
        <div className="grid grid-cols-1 lg:grid-cols-2 min-h-[80vh] lg:min-h-[85vh]">
          {/* Left Column: Left-aligned content group */}
          <div className="relative flex flex-col justify-center px-6 sm:px-10 lg:px-16 xl:px-20 py-16 sm:py-20 lg:py-24 bg-[#F2F2F0]">
            <div className="relative z-10 max-w-xl space-y-7 sm:space-y-8">
              {/* Eyebrow with delicate leading horizontal line detail */}
              <div className="flex items-center gap-3">
                <span className="w-8 h-[1px] bg-[#E36C0A]" aria-hidden="true" />
                <span className="text-[11px] sm:text-xs uppercase tracking-[0.25em] font-bold text-[#18191D]">
                  Enkeltmandsvirksomhed • 25 års erfaring
                </span>
              </div>

              {/* Large airy primary heading with accent color applied to select words */}
              <h1 className="text-3xl sm:text-5xl xl:text-6xl font-extrabold tracking-tight text-[#18191D] leading-[1.12]">
                25 års erfaring med{' '}
                <span className="text-[#E36C0A]">el-arbejde</span>,{' '}
                <span className="text-[#252A6E]">kabeltræk</span> og liftopgaver
              </h1>

              {/* Body text in classic serif typeface for editorial contrast */}
              <p className="font-serif text-base sm:text-lg lg:text-xl text-[#55565C] leading-relaxed">
                Jakob udfører professionelt el-arbejde for private og erhverv med eget materiel — inkl. 8m saxelift, 10,5m mobil bomlift og effektivt kabeltræk.
              </p>

              {/* Button group: Sharp, square corners. Responsive: side-by-side on tablet/desktop, stacked full-width on mobile */}
              <div className="pt-2 flex flex-col sm:flex-row items-stretch sm:items-center gap-3.5">
                {/* Primary button: solid dark background with brand indigo hover */}
                <a
                  href={`tel:${COMPANY_INFO.phoneRaw}`}
                  className="rounded-none bg-[#18191D] hover:bg-[#252A6E] text-white px-8 py-4 text-xs uppercase tracking-[0.2em] font-bold transition-colors inline-flex items-center justify-center gap-3 shadow-md whitespace-nowrap"
                >
                  <span>Ring til Jakob</span>
                  <i className="fa-solid fa-phone text-[11px] text-[#E36C0A]" aria-hidden="true" />
                </a>

                {/* Secondary button: transparent with fine subtle border */}
                <Link
                  to="/el-arbejde"
                  className="rounded-none bg-transparent hover:bg-[#18191D]/5 border border-[#18191D]/30 text-[#18191D] px-8 py-4 text-xs uppercase tracking-[0.2em] font-bold transition-colors inline-flex items-center justify-center gap-2 whitespace-nowrap"
                >
                  <span>Se ydelser</span>
                  <span className="text-sm leading-none" aria-hidden="true">↗</span>
                </Link>
              </div>
            </div>
          </div>

          {/* Right Column: Full-height, full-bleed image with sharp square styling */}
          <div className="relative min-h-[380px] sm:min-h-[460px] lg:min-h-full w-full bg-[#18191D] overflow-hidden">
            <img
              src="https://assets.auxodev.dk/cdn-cgi/image/width=1400,quality=85,format=auto/assets/ea5c97a8-a563-48d7-8fa0-939f59c0351d/197fcec7-f684-426c-9d53-5408d6004b06.jpg"
              alt="jakobelektriker.dk original servicevogn"
              className="absolute inset-0 w-full h-full object-cover object-center"
            />
            {/* Subtle gradient vignette to blend edges cleanly */}
            <div
              className="absolute inset-0 bg-gradient-to-t lg:bg-gradient-to-r from-[#F2F2F0]/20 via-transparent to-black/30 pointer-events-none"
              aria-hidden="true"
            />
          </div>
        </div>
      </section>

      {/* SECTION 2: SERVICES OVERVIEW & 2-COLUMN SPLIT (Layout directly from Construction-21-A) */}
      <section className="bg-white py-20 sm:py-28 px-4 sm:px-6 lg:px-8 border-b border-slate-200">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
            {/* Left Column: 2x2 Interactive Service Grid */}
            <div className="lg:col-span-7 order-2 lg:order-1">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                {serviceGrid.map((srv, idx) => (
                  <Link
                    key={idx}
                    to={srv.slug}
                    className="group bg-[#FFFFFF] rounded-2xl p-6 sm:p-7 border border-slate-200 shadow-xl shadow-slate-200/40 flex flex-col justify-between text-center hover:border-slate-400 hover:shadow-2xl transition-all"
                  >
                    <div>
                      {/* Refined line-art icon in subtle orange */}
                      <div className="w-12 h-12 rounded-xl bg-slate-50 border border-slate-100 mx-auto flex items-center justify-center text-[#E36C0A] text-xl mb-4 group-hover:scale-105 transition-transform">
                        <i className={srv.icon} aria-hidden="true" />
                      </div>
                      <h3 className="font-bold text-slate-900 text-base mb-2 group-hover:text-[#E36C0A] transition-colors">
                        {srv.title}
                      </h3>
                      <p className="text-xs text-[#55565C] leading-relaxed line-clamp-3">
                        {srv.desc}
                      </p>
                    </div>

                    <div className="pt-5 mt-5 border-t border-slate-100 flex justify-center">
                      <span className="w-9 h-9 rounded-full border border-slate-200 flex items-center justify-center text-slate-600 group-hover:bg-[#18191D] group-hover:text-white group-hover:border-[#18191D] transition-all text-xs">
                        <i className="fa-solid fa-arrow-right text-[10px] group-hover:translate-x-0.5 transition-transform" aria-hidden="true" />
                      </span>
                    </div>
                  </Link>
                ))}
              </div>
            </div>

            {/* Right Column: Content & Pitch Block */}
            <div className="lg:col-span-5 order-1 lg:order-2 space-y-6">
              <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-[#55565C]">
                <span className="w-1.5 h-1.5 rounded-full bg-[#E36C0A]" aria-hidden="true" />
                <span>Vores ydelser</span>
              </div>

              <h2 className="text-3xl sm:text-4xl font-extrabold text-slate-900 tracking-tight leading-tight">
                25 års praktisk erfaring med el-arbejde, egne lifte og store kabeltræk
              </h2>

              <p className="text-[#55565C] text-sm sm:text-base leading-relaxed">
                Jakob udfører alle former for almindeligt el-arbejde for både private og erhverv. Han er særligt specialiseret i opgaver i højden og effektive kabeltræk med sit eget maskineri.
              </p>

              <p className="text-[#55565C] text-sm sm:text-base leading-relaxed">
                Ved autorisationskrævende arbejde samarbejder Jakob med en autoriseret el-installatør, så du altid er garanteret en sikker, lovlig og korrekt udført installation.
              </p>

              <div className="pt-2 flex flex-wrap items-center gap-4">
                <a
                  href={`tel:${COMPANY_INFO.phoneRaw}`}
                  className="inline-flex items-center gap-2 rounded-full bg-[#18191D] hover:bg-[#292A30] text-white px-6 py-3 text-xs uppercase tracking-wider font-bold transition-colors"
                >
                  <span>Ring til Jakob: {COMPANY_INFO.phone}</span>
                  <span aria-hidden="true">↗</span>
                </a>
                <Link
                  to="/kontakt"
                  className="inline-flex items-center gap-2 rounded-full border border-slate-300 hover:border-slate-500 text-slate-900 px-6 py-3 text-xs uppercase tracking-wider font-bold transition-colors"
                >
                  <span>Få et tilbud</span>
                  <span aria-hidden="true">↗</span>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 3: SPECIALUDSTYR & MASKINPARK */}
      <section
        className="relative text-white py-20 sm:py-28 px-4 sm:px-6 lg:px-8 border-b border-[#252B66]/60 bg-fixed bg-cover bg-center overflow-hidden"
        style={{
          backgroundImage: `url('https://assets.auxodev.dk/assets/ea5c97a8-a563-48d7-8fa0-939f59c0351d/liftarbejde-omme-bomlift-have.jpg')`,
        }}
      >
        {/* Dark transparent overlay with rich indigo undertone */}
        <div className="absolute inset-0 bg-gradient-to-b from-[#131526]/95 via-[#161930]/92 to-[#111221]/95 backdrop-blur-[2px]" />

        <div className="max-w-7xl mx-auto space-y-12 relative z-10">
          {/* Section Header */}
          <div className="max-w-3xl">
            <span className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-[#B5BBE8] mb-3 bg-[#252A6E]/50 border border-[#3C449E]/40 px-3.5 py-1 rounded-full">
              <span className="w-1.5 h-1.5 rounded-full bg-[#E36C0A]" aria-hidden="true" />
              <span>Udvalgte opgaver & maskinpark</span>
            </span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white tracking-tight">
              Specialudstyr: Lifte, kabeltrækker & minigraver
            </h2>
            <p className="text-[#9FA6D9] text-sm sm:text-base mt-3 leading-relaxed">
              Jakob råder over sit eget specialmateriel. Det betyder hurtig udrykning, ingen fordyrende ekstern liftleje og høj kapacitet på kabeltræk.
            </p>
          </div>

          {/* 3 Dark Architectural Cards */}
          <div className="grid grid-cols-1 md:grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Box 1: Saxelift */}
            <div className="bg-[#1C203A]/85 border border-[#303870]/60 hover:border-[#4E59BA] rounded-2xl p-7 flex flex-col justify-between shadow-xl transition-all">
              <div>
                <span className="inline-block text-[11px] font-bold uppercase tracking-wider text-[#E0E4FF] bg-[#252A6E] border border-[#3C449E]/50 px-3 py-1 rounded-full mb-4">
                  8 meter arbejdshøjde
                </span>
                <h3 className="text-xl font-bold text-white mb-2">
                  Egen Saxelift
                </h3>
                <p className="text-xs sm:text-sm text-[#9FA6D9] leading-relaxed mb-6">
                  Kan komme igennem en helt almindelig dør. Velegnet til både indendørs montage, høje lofter og udendørs facade- og belysningsarbejde. Jakob fragter selv liften direkte til arbejdsstedet.
                </p>
                <ul className="text-xs text-[#9FA6D9] space-y-2 mb-6">
                  <li className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#E36C0A]" aria-hidden="true" />
                    <span>Passer gennem standarddøre</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#E36C0A]" aria-hidden="true" />
                    <span>Ingen dyr udlejer</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#E36C0A]" aria-hidden="true" />
                    <span>Inkluderet i pakkeløsninger</span>
                  </li>
                </ul>
              </div>
              <div className="pt-4 border-t border-[#303870]/50">
                <Link
                  to="/liftarbejde"
                  className="inline-flex items-center gap-2 text-xs uppercase tracking-wider font-bold text-white hover:text-[#E36C0A] transition-colors"
                >
                  <span>Se saxelift specifikationer</span>
                  <span aria-hidden="true">↗</span>
                </Link>
              </div>
            </div>

            {/* Box 2: Omme Bomlift */}
            <div className="bg-[#1C203A]/85 border border-[#303870]/60 hover:border-[#4E59BA] rounded-2xl p-7 flex flex-col justify-between shadow-xl transition-all">
              <div>
                <span className="inline-block text-[11px] font-bold uppercase tracking-wider text-[#E0E4FF] bg-[#252A6E] border border-[#3C449E]/50 px-3 py-1 rounded-full mb-4">
                  10,5 meter arbejdshøjde
                </span>
                <h3 className="text-xl font-bold text-white mb-2">
                  Mobil Omme Bomlift
                </h3>
                <p className="text-xs sm:text-sm text-[#9FA6D9] leading-relaxed mb-6">
                  Kan komme gennem en almindelig havelåge og placere sig præcist i haver, indkørsler og snævre passager. Perfekt til tagrender, facader og træbeskæring i højden.
                </p>
                <ul className="text-xs text-[#9FA6D9] space-y-2 mb-6">
                  <li className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#E36C0A]" aria-hidden="true" />
                    <span>Kan komme gennem en havelåge</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#E36C0A]" aria-hidden="true" />
                    <span>Mobil og skånsom mod underlag</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#E36C0A]" aria-hidden="true" />
                    <span>Tagrender og haveopgaver</span>
                  </li>
                </ul>
              </div>
              <div className="pt-4 border-t border-[#303870]/50">
                <Link
                  to="/liftarbejde"
                  className="inline-flex items-center gap-2 text-xs uppercase tracking-wider font-bold text-white hover:text-[#E36C0A] transition-colors"
                >
                  <span>Se bomlift detaljer</span>
                  <span aria-hidden="true">↗</span>
                </Link>
              </div>
            </div>

            {/* Box 3: Kabeltrækkersystem */}
            <div className="bg-[#1C203A]/85 border border-[#303870]/60 hover:border-[#4E59BA] rounded-2xl p-7 flex flex-col justify-between shadow-xl transition-all">
              <div>
                <span className="inline-block text-[11px] font-bold uppercase tracking-wider text-[#E0E4FF] bg-[#252A6E] border border-[#3C449E]/50 px-3 py-1 rounded-full mb-4">
                  Op til 2 km / dag
                </span>
                <h3 className="text-xl font-bold text-white mb-2">
                  Kabeltræk på minilæsser
                </h3>
                <p className="text-xs sm:text-sm text-[#9FA6D9] leading-relaxed mb-6">
                  Eget kabelaftrækkersystem monteret på minilæsser. Trækker op til 3 kabler ad gangen. Reducerer mandetimer markant og sparer kunden for store entreprenøromkostninger.
                </p>
                <ul className="text-xs text-[#9FA6D9] space-y-2 mb-6">
                  <li className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#E36C0A]" aria-hidden="true" />
                    <span>3 kabler trækkes samtidigt</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#E36C0A]" aria-hidden="true" />
                    <span>Arbejde efter kabelplan</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#E36C0A]" aria-hidden="true" />
                    <span>Pakkeløsning med elektriker</span>
                  </li>
                </ul>
              </div>
              <div className="pt-4 border-t border-[#303870]/50">
                <Link
                  to="/kabeltraek"
                  className="inline-flex items-center gap-2 text-xs uppercase tracking-wider font-bold text-white hover:text-[#E36C0A] transition-colors"
                >
                  <span>Se kabeltrækløsning</span>
                  <span aria-hidden="true">↗</span>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 4: SERVICE HIGHLIGHTS & 8 USPs (Construction-21-A Clean Editorial Module) */}
      <section className="bg-white py-20 sm:py-28 px-4 sm:px-6 lg:px-8 border-b border-slate-200">
        <div className="max-w-7xl mx-auto">
          <div className="max-w-3xl mb-12 text-center mx-auto">
            <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-[#55565C] mb-2">
              <span className="w-1.5 h-1.5 rounded-full bg-[#E36C0A]" aria-hidden="true" />
              <span>Hvorfor vælge Jakob?</span>
            </div>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-slate-900 tracking-tight">
              Jakobs største styrker og faglige fordele
            </h2>
            <p className="text-[#55565C] text-sm sm:text-base mt-2">
              Når du vælger jakobelektriker.dk, får du direkte kontakt til fagpersonen, konkurrencedygtige priser og professionelt maskineri.
            </p>
          </div>

          {/* 8 USPs grid — STRICT REQUIREMENT: Non-interactive, so NO hover effects */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {USPs.map((usp, idx) => (
              <div
                key={idx}
                className="bg-white rounded-2xl p-6 border border-slate-200 shadow-sm flex flex-col justify-between select-none"
              >
                <div>
                  <div className="w-11 h-11 rounded-xl bg-[#1C203A] border border-[#2F3670]/60 text-[#E36C0A] flex items-center justify-center text-base mb-4">
                    <i className={usp.icon} aria-hidden="true" />
                  </div>
                  <h3 className="font-bold text-slate-900 text-base mb-1.5">
                    {usp.title}
                  </h3>
                  <p className="text-xs text-[#55565C] leading-relaxed">
                    {usp.desc}
                  </p>
                </div>
                <div className="pt-4 mt-4 border-t border-slate-100 flex items-center justify-between text-[11px] text-[#86878D] font-medium">
                  <span>Jakobs fordel</span>
                  <i className="fa-solid fa-check text-[#5D69DC]" aria-hidden="true" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 5: VERBATIM SCRAPE PROSE STATEMENT (Construction-21-A Testimonial module layout) */}
      <section className="relative bg-gradient-to-br from-[#141628] via-[#181B34] to-[#121320] text-white py-20 sm:py-28 px-4 sm:px-6 lg:px-8 border-b border-[#252B66]/60 overflow-hidden">
        {/* Subtle background glow matching brand colors */}
        <div className="absolute top-1/4 -left-20 w-96 h-96 bg-[#252A6E]/25 rounded-full blur-3xl pointer-events-none" aria-hidden="true" />

        <div className="max-w-7xl mx-auto relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            {/* Left Content with Prominent Oversized Quote Icon */}
            <div className="lg:col-span-7 space-y-6">
              <i className="fa-solid fa-quote-left text-5xl text-[#E36C0A]/80 block" aria-hidden="true" />

              <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-[#B5BBE8] bg-[#252A6E]/50 border border-[#3C449E]/40 px-3.5 py-1 rounded-full">
                <span className="w-1.5 h-1.5 rounded-full bg-[#E36C0A]" aria-hidden="true" />
                <span>Velkommen til jakobelektriker.dk</span>
              </div>

              <h2 className="text-3xl sm:text-4xl font-extrabold text-white tracking-tight">
                jakobelektriker er en enkeltmands forretning
              </h2>

              {/* Exact verbatim scrape text - strictly preserved */}
              <div className="text-[#A4ACDE] text-sm sm:text-base leading-relaxed space-y-4">
                <p>
                  <strong className="text-white">Jeg udføre alle former for el-arbejde i private hjem. Jeg køre uafhængigt af en el- installatør. De el-opgaver der kræver en el-installatør løses også. Sådanne opgaver tilbydes udført i samarbejde med en El-installatør.</strong>
                </p>
                <p>
                  <strong className="text-white">Jeg har specialiseret mig i opgaver i højden og billigst på lange kabeltræk. Jeg har egen saxelift med en max arbejdshøjde på 8 meter, den kan komme igennem en almindelig dør. Jeg fragter selv liften til og fra arbejdssted. Jeg er ligeledes mobil med en 10,5 meters Omme bom lift. Her sparer jeg kunden for mange sure kr. til en udlejer. Hvis dine lamper i højden inde som ude skal skiftes kan jeg give tilbud på danske designer lamper inkl. montage.</strong>
                </p>
                <p>
                  <strong className="text-white">Samarbejde med både El-installatør virksomheder og andre Håndværksmestre er en stor del af min forretning. Mine 25 års erfaring som elektriker, både som service elektriker i mindre og større private opgaver og især som ny byg og renoverings elektriker for både privat og erhverv, gør jeg har prøvet det meste. Fra opgaver indenfor total renovering af sommerhuset til at planlægge, lede og fordele arbejdet i store byggesager der har taget op mod 3 år at gennemføre.</strong>
                </p>
              </div>

              <div className="pt-3 flex flex-wrap items-center gap-4">
                <Link
                  to="/om-jakob"
                  className="inline-flex items-center gap-2 rounded-full border border-[#3C449E]/70 hover:bg-[#252A6E] hover:border-[#4E59BA] text-white px-6 py-3 text-xs uppercase tracking-wider font-bold transition-all shadow-sm"
                >
                  <span>Læs mere om Jakob</span>
                  <span aria-hidden="true">↗</span>
                </Link>
                <a
                  href={`tel:${COMPANY_INFO.phoneRaw}`}
                  className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-[#DCE0FF] hover:text-[#E36C0A] transition-colors"
                >
                  <i className="fa-solid fa-phone text-xs" aria-hidden="true" />
                  <span>Ring: {COMPANY_INFO.phone}</span>
                </a>
              </div>
            </div>

            {/* Right: Service van image */}
            <div className="lg:col-span-5">
              <div className="rounded-2xl overflow-hidden border border-[#303870]/60 shadow-2xl bg-[#121322]">
                <img
                  src={getOptimizedImageUrl(vanRear, { width: 800, quality: 80 })}
                  alt="jakobelektriker.dk servicevogn med information om kabelplan og 22 32 99 79"
                  className="w-full h-auto lg:min-h-[440px] lg:h-[480px] object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 6: PRICING & PACKAGE DEAL MODULE */}
      <section className="bg-white py-20 sm:py-24 px-4 sm:px-6 lg:px-8 border-b border-slate-200">
        <div className="max-w-7xl mx-auto">
          <PriceBanner showCablePrice={true} />
        </div>
      </section>

      {/* SECTION 7: HURTIG KONTAKT (Restyled after Design Reference #091537b2-dc7c-480a-b7ac-e7477e021791) */}
      <section
        className="relative text-white py-24 sm:py-32 px-4 sm:px-6 lg:px-8 border-t border-[#252B66]/60 bg-cover bg-center overflow-hidden"
        style={{
          backgroundImage: `url('https://assets.auxodev.dk/assets/ea5c97a8-a563-48d7-8fa0-939f59c0351d/197fcec7-f684-426c-9d53-5408d6004b06.jpg')`,
        }}
      >
        {/* Dark overlay with deep indigo / carbon tone to ensure readability */}
        <div className="absolute inset-0 bg-gradient-to-b from-[#131526]/95 via-[#171A34]/92 to-[#101120]/96 backdrop-blur-sm" />

        <div className="max-w-7xl mx-auto relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
            {/* Left Column (approximately 50/50 split on desktop) */}
            <div className="lg:col-span-6 space-y-8">
              <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-[#E0E4FF] bg-[#252A6E]/80 border border-[#3C449E]/50 px-3.5 py-1.5 rounded-full">
                <span className="w-1.5 h-1.5 rounded-full bg-[#E36C0A]" aria-hidden="true" />
                <span>Kom i kontakt</span>
              </div>

              {/* Display heading with same font and style as PriceBanner heading */}
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-white leading-tight">
                Klar til at hjælpe med din næste el-opgave
              </h2>

              {/* Substantial block of body text */}
              <div className="space-y-4 text-sm sm:text-base text-[#DCE0FF] leading-relaxed max-w-xl font-sans">
                <p>
                  Jakob svarer altid personligt på henvendelser. Uanset om det drejer sig om almindelige el-installationer i boligen, omfattende kabeltræk i store byggerier eller arbejde i højden med egen lift, får du direkte rådgivning og hurtig afklaring.
                </p>
                <p>
                  Hos jakobelektriker.dk er en time en time – 485 kr. ex. moms inklusive servicevogn og uden skjulte gebyrer. Ring gerne for en uforpligtende snak om opgavens omfang.
                </p>
              </div>

              {/* Smaller attribution / link */}
              <div className="pt-2 text-xs text-[#9FA6D9] font-mono tracking-wider">
                <span>CVR: {COMPANY_INFO.cvr} • {COMPANY_INFO.address}, {COMPANY_INFO.postal} {COMPANY_INFO.city}</span>
              </div>

              {/* Outlined primary action button (ghost button) */}
              <div className="pt-2">
                <a
                  href={`tel:${COMPANY_INFO.phoneRaw}`}
                  className="inline-flex items-center gap-3 rounded-full border border-white/40 hover:border-white hover:bg-[#252A6E]/40 text-white font-bold text-xs uppercase tracking-widest px-8 py-4 transition-all"
                >
                  <i className="fa-solid fa-phone text-[#E36C0A]" aria-hidden="true" />
                  <span>Ring direkte: {COMPANY_INFO.phone}</span>
                </a>
              </div>
            </div>

            {/* Right Column: Contact form with bottom-border minimal styling */}
            <div className="lg:col-span-6 bg-[#16192E]/85 border border-[#303870]/60 rounded-3xl p-8 sm:p-12 shadow-2xl backdrop-blur-md">
              <div className="mb-8">
                <span className="text-[11px] font-bold uppercase tracking-widest text-[#E0E4FF] block mb-1">
                  Send en forespørgsel
                </span>
                <p className="text-xs sm:text-sm text-[#9FA6D9]">
                  Udfyld formularen herunder, så vender Jakob tilbage hurtigst muligt.
                </p>
              </div>

              <ContactFormMinimal />
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}
